import { useCallback, useEffect, useState, type CSSProperties } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../auth/AuthContext";
import { PanelLoading } from "../../components/Loading";
import { LotQualityBadge } from "../../components/LotQualityBadge";
import { useToast } from "../../components/Toast";
import { useConfirm } from "../../components/ConfirmDialog";
import { Select } from "../../components/Select";
import { formatDate } from "../../lib/format";
import Ic from "../../components/icons.jsx";
import type { ApiError } from "../../api/client";
import { dispatchOrderApi, type DispatchOrder } from "../../api/dispatchOrders";
import { DispatchErpStatus } from "../../components/orders/DispatchErpStatus";
import { adminApi } from "../../api/admin";

type Firma = { id: string; name: string };

/**
 * Nalog za otpremu, strana prodaje.
 *
 * Prodaja ga otvara, popisuje šta ide na njega i šalje ga kvalitetu. Ekran NEMA polje za LOT, i to je
 * namerno — koji LOT ide odlučuje kvalitet, a polje ovde bi dalo prodaji da odgovori na pitanje zbog
 * kojeg cela kapija postoji.
 *
 * Nalog koji je kod kvaliteta prodaja ne menja. Da bi ga menjala, prvo ga vrati sebi, a to vraćanje
 * briše svaku odluku o LOT-u — jer je puštanje dato za ovu količinu i ovaj artikal, pa ne znači isto
 * ako se oni promene.
 */
export default function DispatchOrdersPage() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  // Nalog je prodajni dokument. Ko ga sme otvoriti drzi dispatch_orders:manage; ruta trazi samo
  // :view, pa se razdvaja gledanje od otvaranja i ovde, a ne samo na servisu.
  const smeDaOtvara = hasPermission("dispatch_orders:manage");
  const toast = useToast();
  const confirm = useConfirm();

  const [orders, setOrders] = useState<DispatchOrder[] | null>(null);
  // An order's screen opens a dispatch order it just made straight on it.
  const [params] = useSearchParams();
  const [openId, setOpenId] = useState<string | null>(params.get("open"));
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const rows = await dispatchOrderApi.list();
      setOrders(rows);
      if (!openId && rows.length > 0) setOpenId(rows[0].id);
    } catch (e) {
      setError((e as ApiError).message);
    }
  }, [openId]);

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function replace(updated: DispatchOrder) {
    setOrders((prev) => (prev ?? []).map((o) => (o.id === updated.id ? updated : o)));
  }

  async function act(fn: () => Promise<DispatchOrder>) {
    try {
      replace(await fn());
    } catch (e) {
      toast.error((e as ApiError).message);
    }
  }

  if (error) return <div className="empty">{error}</div>;
  if (!orders) return <PanelLoading text={t("common.loading", "Učitavanje…")} />;

  const open = orders.find((o) => o.id === openId) ?? null;

  return (
    <div className="fadein" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="page-head">
        <div>
          <h1>{t("dispatchOrders.title", "Nalozi za otpremu")}</h1>
          <div className="sub">
            {t(
              "dispatchOrders.lead",
              "Prodaja otvara nalog i popisuje šta ide na njega; koji LOT ide odlučuje kvalitet.",
            )}
          </div>
        </div>
        {smeDaOtvara && (
          <div className="page-actions">
            <button className="btn primary" type="button" onClick={() => setCreating(true)}>
              <Ic.plus /> {t("dispatchOrders.new", "Nov nalog")}
            </button>
          </div>
        )}
      </div>

      {smeDaOtvara && creating && (
        <CreateForm
          onDone={async () => {
            setCreating(false);
            await load();
          }}
          onCancel={() => setCreating(false)}
        />
      )}

      {orders.length === 0 && !creating ? (
        <div className="card">
          <div className="empty">{t("dispatchOrders.none", "Nema otvorenih naloga za otpremu.")}</div>
        </div>
      ) : (
        <div className="catalog-layout">
          <aside className="cat-side">
            <div className="group">
              <h4>{t("dispatchOrders.open", "Otvoreni nalozi")}</h4>
              <div className="cat-list">
                {orders.map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    className={o.id === openId ? "active" : ""}
                    onClick={() => setOpenId(o.id)}
                    title={`${o.companyName ?? ""} · ${formatDate(o.issueDate)}`}
                  >
                    <span
                      style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    >
                      {o.number} · {statusTekst(t, o.status)}
                    </span>
                    {o.lines.length > 0 && <span className="n">{o.lines.length}</span>}
                  </button>
                ))}
              </div>
            </div>
          </aside>

          <section style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 16 }}>
            {!open ? (
              <div className="card">
                <div className="empty">{t("dispatchOrders.pickOne", "Izaberite nalog sa spiska.")}</div>
              </div>
            ) : (
              <OrderPanel order={open} onAct={act} confirmFn={confirm} />
            )}
          </section>
        </div>
      )}
    </div>
  );
}

function statusTekst(t: ReturnType<typeof useTranslation>["t"], s: DispatchOrder["status"]): string {
  switch (s) {
    case "DRAFT":
      return t("dispatchRelease.status.DRAFT", "u pripremi");
    case "AWAITING_QUALITY":
      return t("dispatchRelease.status.AWAITING_QUALITY", "čeka kvalitet");
    case "RELEASED":
      return t("dispatchRelease.status.RELEASED", "pušten");
    case "ON_HOLD":
      return t("dispatchRelease.status.ON_HOLD", "zadržan");
    case "CANCELLED":
      return t("dispatchRelease.status.CANCELLED", "otkazan");
  }
}

function OrderPanel({
  order,
  onAct,
  confirmFn,
}: {
  order: DispatchOrder;
  onAct: (fn: () => Promise<DispatchOrder>) => Promise<void>;
  confirmFn: ReturnType<typeof useConfirm>;
}) {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("section:dispatch_orders:write");
  const canFull = hasPermission("section:dispatch_orders:full");
  const [addingLine, setAddingLine] = useState(false);
  const draft = order.status === "DRAFT";

  async function returnToDraft() {
    const odluka = order.lines.filter((l) => l.released || l.held).length;
    const ok = await confirmFn({
      message:
        odluka > 0
          ? t(
              "dispatchOrders.confirmReturnWithDecisions",
              "Vraćanje briše {{n}} odluku kvaliteta o LOT-u na ovom nalogu. Puštanje je dato za ovu količinu i ovaj artikal, pa ne važi ako se oni promene. Da vratim?",
              { n: odluka },
            )
          : t("dispatchOrders.confirmReturn", "Da vratim nalog u pripremu?"),
    });
    if (!ok) return;
    await onAct(() => dispatchOrderApi.returnToDraft(order.id));
  }

  return (
    <>
      <div className="card" style={order.status === "ON_HOLD" ? ivicaUpozorenje : undefined}>
        <header className="card-h">
          <div>
            <h3>{order.number}</h3>
            <div className="sub">
              {order.companyName ?? "—"} · {formatDate(order.issueDate)}
              {order.vehicle ? ` · ${order.vehicle}` : ""}
              {order.driver ? ` · ${order.driver}` : ""}
            </div>
          </div>
          <span className="tag" style={order.status === "ON_HOLD" ? znakUpozorenje : undefined}>
            {statusTekst(t, order.status)}
          </span>
        </header>
        {order.status === "AWAITING_QUALITY" && (
          <div className="card-b" style={{ paddingTop: 12, paddingBottom: 12 }}>
            <span className="tag">
              {t("dispatchOrders.withQuality", "Nalog je kod kvaliteta. Dok je tamo, prodaja ga ne menja.")}
            </span>
          </div>
        )}
        {order.status === "RELEASED" && (
          <div className="card-b" style={{ paddingTop: 12, paddingBottom: 12 }}>
            <span className="tag" style={znakOk}>
              {t("dispatchOrders.releasedAll", "Kvalitet je pustio sve LOT-ove. Utovar može.")}
            </span>
            <div style={{ marginTop: 10 }}>
              <DispatchErpStatus order={order} />
            </div>
          </div>
        )}
        {order.status === "ON_HOLD" && (
          <div className="card-b">
            <strong style={{ color: "var(--warn)" }}>
              {t("dispatchOrders.heldTitle", "Kvalitet je zadržao LOT")}
            </strong>
            {order.lines
              .filter((l) => l.held)
              .map((l) => (
                <div key={l.id} style={{ marginTop: 4, fontSize: 13 }}>
                  {l.position}. {l.productName} — {l.holdReason}
                  {l.heldBy ? <span style={tiho}> ({l.heldBy})</span> : null}
                </div>
              ))}
          </div>
        )}
      </div>

      <div className="card">
        <header className="card-h">
          <h3>{t("dispatchOrders.lines", "Šta ide na nalog")}</h3>
        </header>
      {order.lines.length === 0 ? (
        <div className="empty">{t("dispatchOrders.noLines", "Nalog još nema stavki.")}</div>
      ) : (
        <div className="card-b tight">
        <table className="tbl">
          <thead>
            <tr>
              <th>{t("dispatchOrders.col.product", "Artikal")}</th>
              <th>{t("dispatchOrders.col.quantity", "Količina")}</th>
              <th>{t("dispatchOrders.col.lot", "LOT")}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {order.lines.map((l) => (
              <tr key={l.id}>
                <td>
                  {l.position}. {l.productName ?? l.erpProductId ?? "—"}
                </td>
                <td style={tiho}>
                  {l.quantity ?? "—"} {l.unit ?? ""}
                </td>
                <td>
                  {l.lotDesignation ? (
                    <>
                      <span style={{ fontFamily: "var(--mono)" }}>{l.lotDesignation}</span>{" "}
                      <LotQualityBadge status={l.lotQualityStatus} note={l.lotQualityNote} />
                      {l.released && (
                        <span className="tag" style={znakOk}>
                          {t("dispatchRelease.status.RELEASED", "pušten")}
                        </span>
                      )}
                      {l.held && (
                        <span className="tag" style={znakUpozorenje}>
                          {t("dispatchRelease.status.ON_HOLD", "zadržan")}
                        </span>
                      )}
                    </>
                  ) : (
                    <span style={tiho}>{t("dispatchOrders.lotByQuality", "odrediće kvalitet")}</span>
                  )}
                </td>
                <td style={{ textAlign: "right" }}>
                  {canFull && draft && (
                    <button
                      className="btn sm"
                      type="button"
                      onClick={() => void onAct(() => dispatchOrderApi.removeLine(order.id, l.id))}
                    >
                      {t("common.remove", "Skloni")}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
      </div>

      {canWrite && draft && addingLine && (
        <LineForm
          orderId={order.id}
          onDone={async (updated) => {
            setAddingLine(false);
            await onAct(async () => updated);
          }}
          onCancel={() => setAddingLine(false)}
        />
      )}

      <div className="toolbar" style={{ marginBottom: 0 }}>
        {canWrite && draft && !addingLine && (
          <button className="btn" type="button" onClick={() => setAddingLine(true)}>
            <Ic.plus /> {t("dispatchOrders.addLine", "Dodaj stavku")}
          </button>
        )}
        {canWrite && draft && order.lines.length > 0 && (
          <button
            className="btn primary"
            type="button"
            onClick={() => void onAct(() => dispatchOrderApi.sendToQuality(order.id))}
          >
            {t("dispatchOrders.send", "Pošalji kvalitetu")}
          </button>
        )}
        {canFull && !draft && order.status !== "CANCELLED" && !order.erpId && (
          <button className="btn" type="button" onClick={() => void returnToDraft()}>
            {t("dispatchOrders.returnToDraft", "Vrati u pripremu")}
          </button>
        )}
      </div>
    </>
  );
}

function CreateForm({ onDone, onCancel }: { onDone: () => void | Promise<void>; onCancel: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [firme, setFirme] = useState<Firma[]>([]);
  const [upit, setUpit] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [vehicle, setVehicle] = useState("");
  const [driver, setDriver] = useState("");
  const [deliveryPlace, setDeliveryPlace] = useState("");
  const [busy, setBusy] = useState(false);

  const traziFirme = useCallback(async (term: string) => {
    try {
      const res = await adminApi.listCompanies(0, 20, "CUSTOMER", term || undefined);
      setFirme(res.content.map((c) => ({ id: c.id, name: c.name })));
    } catch (e) {
      toast.error((e as ApiError).message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void traziFirme("");
  }, [traziFirme]);

  async function submit() {
    setBusy(true);
    try {
      await dispatchOrderApi.create({
        companyId,
        vehicle: vehicle.trim() || null,
        driver: driver.trim() || null,
        deliveryPlace: deliveryPlace.trim() || null,
      });
      await onDone();
    } catch (e) {
      toast.error((e as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <div className="card-b">
      <div style={mreza}>
        <div style={{ gridColumn: "1 / -1" }}>
          <div style={{ ...tiho, marginBottom: 4 }}>{t("dispatchOrders.field.company", "Kupac")}</div>
          <input
            className="input"
            value={upit}
            onChange={(e) => {
              setUpit(e.target.value);
              void traziFirme(e.target.value);
            }}
            placeholder={t("dispatchOrders.searchCompany", "Traži kupca po imenu")}
            style={{ marginBottom: 6 }}
          />
          <Select
            value={companyId}
            onChange={setCompanyId}
            placeholder={t("quality.plan.pickTest", "— izaberite —")}
            options={firme.map((f) => ({ value: f.id, label: f.name }))}
          />
        </div>
        <div>
          <div style={{ ...tiho, marginBottom: 4 }}>{t("dispatchOrders.field.vehicle", "Vozilo")}</div>
          <input className="input" value={vehicle} onChange={(e) => setVehicle(e.target.value)} />
        </div>
        <div>
          <div style={{ ...tiho, marginBottom: 4 }}>{t("dispatchOrders.field.driver", "Vozač")}</div>
          <input className="input" value={driver} onChange={(e) => setDriver(e.target.value)} />
        </div>
        <div>
          <div style={{ ...tiho, marginBottom: 4 }}>{t("dispatchOrders.field.deliveryPlace", "Mesto isporuke")}</div>
          <input className="input" value={deliveryPlace} onChange={(e) => setDeliveryPlace(e.target.value)} />
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 12, justifyContent: "flex-end" }}>
        <button className="btn" type="button" onClick={onCancel} disabled={busy}>
          {t("common.cancel", "Odustani")}
        </button>
        <button className="btn primary" type="button" onClick={() => void submit()} disabled={busy || !companyId}>
          {t("dispatchOrders.create", "Otvori nalog")}
        </button>
      </div>
      </div>
    </div>
  );
}

function LineForm({
  orderId,
  onDone,
  onCancel,
}: {
  orderId: string;
  onDone: (updated: DispatchOrder) => void | Promise<void>;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [productName, setProductName] = useState("");
  const [erpProductId, setErpProductId] = useState("");
  const [quantity, setQuantity] = useState("");
  const [unit, setUnit] = useState("kg");
  const [busy, setBusy] = useState(false);

  async function submit() {
    setBusy(true);
    try {
      const updated = await dispatchOrderApi.addLine(orderId, {
        productName: productName.trim(),
        erpProductId: erpProductId.trim() || null,
        quantity: Number(quantity.replace(",", ".")),
        unit: unit.trim() || "kg",
      });
      await onDone(updated);
    } catch (e) {
      toast.error((e as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <div className="card-b">
      <div style={mreza}>
        <div style={{ gridColumn: "1 / -1" }}>
          <div style={{ ...tiho, marginBottom: 4 }}>{t("dispatchOrders.col.product", "Artikal")}</div>
          <input className="input" value={productName} onChange={(e) => setProductName(e.target.value)} />
        </div>
        <div>
          <div style={{ ...tiho, marginBottom: 4 }}>{t("dispatchOrders.field.erpId", "Šifra u ERP-u")}</div>
          <input className="input" value={erpProductId} onChange={(e) => setErpProductId(e.target.value)} />
        </div>
        <div>
          <div style={{ ...tiho, marginBottom: 4 }}>{t("dispatchOrders.col.quantity", "Količina")}</div>
          <input className="input" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
        </div>
        <div>
          <div style={{ ...tiho, marginBottom: 4 }}>{t("dispatchOrders.field.unit", "Jedinica")}</div>
          <input className="input" value={unit} onChange={(e) => setUnit(e.target.value)} />
        </div>
      </div>
      <p style={{ ...tiho, marginTop: 10 }}>
        {t("dispatchOrders.lotLater", "LOT se ne upisuje ovde — odrediće ga kvalitet kad nalog stigne do njega.")}
      </p>
      <div style={{ display: "flex", gap: 8, marginTop: 12, justifyContent: "flex-end" }}>
        <button className="btn" type="button" onClick={onCancel} disabled={busy}>
          {t("common.cancel", "Odustani")}
        </button>
        <button
          className="btn primary"
          type="button"
          onClick={() => void submit()}
          disabled={busy || !productName.trim() || !quantity.trim()}
        >
          {t("common.save", "Sačuvaj")}
        </button>
      </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ stil */

/*
 * Ekran stoji na sistemu: `catalog-layout`, `cat-side`, `group`, `cat-list`, `card`, `card-h`,
 * `card-b`, `tbl`, `tag`, `empty`, `toolbar`. Ovde je samo ono čega u sistemu nema — boja ishoda na
 * znački i na ivici kartice, i mreža polja u obrascu.
 */

const tiho: CSSProperties = { color: "var(--ink-3)", fontSize: 12.5 };

const znakUpozorenje: CSSProperties = {
  borderColor: "var(--warn)",
  color: "var(--warn)",
  background: "transparent",
};
const znakOk: CSSProperties = {
  borderColor: "var(--ok)",
  color: "var(--ok)",
  background: "transparent",
};

/** Zadržan nalog se vidi po ivici pre nego što se pročita zašto. */
const ivicaUpozorenje: CSSProperties = { borderColor: "var(--warn)" };

const mreza: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
  gap: 12,
};
